/* eslint-disable no-prototype-builtins */
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { data } from './constants';
import './index.css';
import withDragDropContext from './withDnDContext';
import moment from 'moment';
import PropTypes from 'prop-types';
import Flex from 'components/common/Flex';
import { getFullScheduleAllData } from 'nursd-dashboard/api/fullSchedule';
import { getAllFacilityOrgList } from 'nursd-dashboard/api/organizationTable';
import { useAuth } from 'nursd-dashboard/context/AuthContext';
import CalendarEventModal from './CalendarEventModal';
import { postJobTable, updateJobTable } from 'nursd-dashboard/api/jobTable';
import { socket } from 'nursd-dashboard/api/socket';
import { postNurseNotification } from 'nursd-dashboard/api/nurseNotification';
import { sendNotification } from 'nursd-dashboard/api/webNotification';
import { DateFormat } from 'nursd-dashboard/utils/function';
import { toast } from 'react-toastify';

// Example utility function to calculate total hours (implement your logic)
const calculateTotalHours = job => {
  // Example: sum the hours from all days
  return ['day1', 'day2', 'day3', 'day4', 'day5', 'day6', 'day7'].reduce(
    (total, day) => {
      return total + job[day].length; // Assuming length represents hours; adjust as needed
    },
    0
  );
};

const overLapCheck = (startDate, endDate, startTime, endTime, array) => {
  const startDateToCheck = moment(startDate).format('YYYY-MM-DD');
  const endDateToCheck = moment(endDate).format('YYYY-MM-DD');
  const matchFoundDate = array?.some(obj => {
    return (
      (obj.date <= startDateToCheck && startDateToCheck <= obj.endDate) ||
      (obj.date <= endDateToCheck && endDateToCheck <= obj.endDate) ||
      (startDateToCheck <= obj.date && obj.date <= endDateToCheck) || // Corrected this line
      (startDateToCheck <= obj.endDate && obj.endDate <= endDateToCheck) // Added this line
    );
  });

  function isOverlappingTime(newEvent, existingEvents) {
    const newStart = moment(
      newEvent.startDate + ' ' + newEvent.startTime,
      'YYYY-MM-DD h:mm a'
    );
    const newEnd = moment(
      newEvent.endDate + ' ' + newEvent.endTime,
      'YYYY-MM-DD h:mm a'
    );

    if (!newStart.isValid() || !newEnd.isValid()) {
      return true;
    }

    for (const existingEvent of existingEvents) {
      const nStart = moment(newStart.toString());
      const nEnd = moment(newEnd.toString());

      const existingStart = moment(
        existingEvent.startTime,
        'YYYY-MM-DD h:mm a'
      );
      const existingEnd = moment(existingEvent.endTime, 'YYYY-MM-DD h:mm a');

      // console.log(nStart, existingStart, nEnd, existingEnd);
      if (!existingStart.isValid() || !existingEnd.isValid()) {
        return true;
      }

      if (
        nStart.isSameOrBefore(existingEnd) &&
        nEnd.isSameOrAfter(existingStart)
      ) {
        return true;
      }
    }
    return false;
  }

  const newEvent = {
    startDate: startDate,
    endDate: endDate,
    startTime: moment(startTime)?.format('h:mm a'),
    endTime: moment(endTime)?.format('h:mm a')
  };

  let obj = {
    availableDate: !matchFoundDate,
    availableTime:
      array?.length !== 0 ? !isOverlappingTime(newEvent, array) : true
  };

  return obj;
};

const MovableItem = ({
  index,
  name,
  item,
  currentColumnName,
  moveCardHandler,
  result,
  setResult,
  handleEventClick,
  setCopyContent
}) => {
  const { authToken } = useAuth();

  const updateNurseToJob = async (date, job, nurse) => {
    let startDate = date;
    let endDate = date;
    let startTime = moment(
      date + ' ' + moment(job?.startTime)?.format('h:mm a'),
      'YYYY-MM-DD h:mm a'
    );
    let endTime = moment(
      date + ' ' + moment(job?.endTime)?.format('h:mm a'),
      'YYYY-MM-DD h:mm a'
    );

    await updateJobTable(authToken, job?.id, {
      startDate: startDate,
      endDate: endDate,
      startTime: startTime?.toString(),
      endTime: endTime?.toString(),
      startDateTimeStamp: startTime.valueOf().toString(),
      jobFinalSelectionNurseId: nurse?.uniqueId?.toString(),
      jobAcceptedNurses: [nurse?.uniqueId?.toString()],
      approved_manager: job?.jobPostingTableFacilityTableId,
      jobCompletedBy: nurse?.poolType,
      jobStatus: 'Nurse Assigned'
    })
      .then(async e => {
        let date = `${moment(e?.startTime).format('hh:mm a')}-${moment(
          e?.endTime
        ).format('hh:mm a')}`;
        if (nurse?.mobileId) {
          sendNotification(authToken, {
            expoPushToken: nurse?.mobileId,
            title: `“Direct ${e?.jobType} Assignment” (Check Upcoming Jobs)`,
            body: `${e?.jobType} for ${DateFormat(e?.startDate)} ${date}`,
            data: { jobId: e?.id, screen: 'JobDetailScreen' }
          });
        }
        await postNurseNotification(
          authToken,
          nurse?.id,
          '',
          `“Direct ${e?.jobType} Assignment” (Check Upcoming Jobs)`,
          `${e?.jobType} for ${DateFormat(e?.startDate)} ${date}`,
          'JobDetailsScreen',
          {
            id: e?.id
          },
          false,
          false,
          'green',
          ''
        );
        socket?.emit('tableNotificationInsert', {
          table: 'NurseNotificationTable',
          id: nurse?.id
        });
        toast.success(`Assigned successfully.`, {
          theme: 'colored'
        });
      })
      .catch(e => console.log(e));
  };

  const changeItemColumn = (currentItem, newDropItem) => {
    if (
      moment(newDropItem?.date).format('MM/DD/YYYYY') >=
      moment().format('MM/DD/YYYYY')
    ) {
      if (newDropItem?.nurseId !== 'not') {
        const updatedResult = result?.map(job => {
          let currentDay = currentItem?.currentColumnName; // e.g. "day3"
          let newDay = newDropItem?.day; // e.g. "day4"

          // Find the object from current day array
          let obj = job[currentDay]?.find(i => i?.id === currentItem?.id);

          // Find the new nurse by nurseId
          let newNurse = result.find(
            nurseJob => nurseJob?.id?.uniqueId === newDropItem?.nurseId
          );

          if (obj && newNurse) {
            let check = overLapCheck(
              newDropItem?.date,
              newDropItem?.date,
              obj?.startTime,
              obj?.endTime,
              newNurse[newDay]
            );

            if (check?.availableDate && check.availableTime) {
              updateNurseToJob(newDropItem?.date, obj, newNurse['id']);
              // Create the updated object for the new day
              let newObj = {
                ...obj,
                jobFinalSelectionNurseId: newDropItem?.nurseId,
                jobStatus: 'Nurse Assigned'
              };

              // Remove object from current day array
              job[currentDay] = job[currentDay]?.filter(
                i => i?.id !== currentItem?.id
              );

              // Add object to the new day array in the new nurse's schedule
              newNurse[newDay] = [...(newNurse[newDay] || []), newObj];

              // Update total hours for the current job
              job.totalHours = calculateTotalHours(job);

              // Update total hours for the new nurse job
              newNurse.totalHours = calculateTotalHours(newNurse);

              return job; // return either modified or unchanged job
            } else {
              toast.warning(`Schedule time conflict!`, {
                theme: 'colored'
              });
            }
          }
        });

        setResult(updatedResult); // Update the state with the modified result
      }
    } else {
      toast.warning(`You can't assign the job to past date!`, {
        theme: 'colored'
      });
    }
  };

  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: 'Our first type',
    hover(item, monitor) {
      if (!ref?.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      item.index = hoverIndex;
    }
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'Our first type',
    canDrag: () => {
      if (item?.jobStatus === 'Open') {
        return true;
      } else {
        return false;
      }
    },
    item: {
      index,
      id: item?.id,
      jobFinalSelectionNurseId: item?.jobFinalSelectionNurseId,
      start: item?.startDate,
      end: item?.endDate,
      name,
      currentColumnName
    },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult) {
        changeItemColumn(item, dropResult);
        moveCardHandler(item, dropResult);
      }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0.4 : 1;

  drag(drop(ref));

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef(null);

  const handleRightClick = e => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };

  // Hide the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={ref}>
      {menuVisible && (
        <div
          ref={menuRef}
          style={{
            cursor: 'pointer',
            backgroundColor: '#ddd',
            width: 80,
            height: 30,
            borderRadius: 5,
            textAlign: 'center',
            position: 'absolute',
            top: menuPosition.y - 149,
            left: menuPosition.x,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => {
            setMenuVisible(false);
            setCopyContent(item);
          }}
        >
          Copy
        </div>
      )}
      <div
        onContextMenu={handleRightClick}
        onClick={() => handleEventClick(item)}
        style={{
          backgroundColor:
            item?.jobStatus === 'Unfulfilled'
              ? '#888'
              : item?.jobStatus === 'Open' ||
                item?.jobStatus === 'Pending Assignment'
              ? '#ffff00'
              : item?.jobStatus === 'Completed'
              ? '#00b359'
              : item?.jobStatus === 'Nurse Assigned' ||
                item?.jobStatus === 'In-Progress' ||
                item?.jobStatus === 'Pending Approval'
              ? '#2775BD'
              : item?.jobStatus === 'Missed' ||
                item?.jobStatus === 'Pending Clock Out'
              ? '#fc0505'
              : '#888',
          color:
            item?.jobStatus === 'Unfulfilled'
              ? '#fff'
              : item?.jobStatus === 'Open' ||
                item?.jobStatus === 'Pending Assignment'
              ? '#000'
              : item?.jobStatus === 'Completed'
              ? '#fff'
              : item?.jobStatus === 'Nurse Assigned' ||
                item?.jobStatus === 'In-Progress' ||
                item?.jobStatus === 'Pending Approval'
              ? '#fff'
              : item?.jobStatus === 'Missed' ||
                item?.jobStatus === 'Pending Clock Out'
              ? '#fff'
              : '#fff',
          opacity,
          cursor: 'pointer',
          padding: 5,
          margin: 5,
          borderRadius: 5
        }}
        className={`font-medium`}
      >
        {name}
      </div>
    </div>
  );
};

const Column = ({ children, className, title, value, date, day }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'Our first type',
    drop: () => ({ date: date, day: day, nurseId: value }),
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  const getBackgroundColor = () => {
    if (isOver) {
      if (canDrop) return 'rgb(220 ,252 ,231)';
      return 'rgb(255,188,188)';
    }
    return '';
  };

  return (
    <div
      ref={drop}
      className={className}
      style={{
        backgroundColor: getBackgroundColor(),
        padding: 5,
        flex: 1,
        height: '100%'
      }}
    >
      <h6 className="font-medium">{title}</h6>
      <Flex className={'flex-wrap'}>{children}</Flex>
    </div>
  );
};

const RosterPlan = () => {
  const { authToken, location, organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weekdays, setWeekDays] = useState([]);
  const [result, setResult] = useState([]);
  const [facilityId, setFacilityId] = useState(undefined);
  const [isOpenEventModal, setIsOpenEventModal] = useState(false);
  const [modalEventContent, setModalEventContent] = useState({});
  const [copyContent, setCopyContent] = useState(undefined);
  const [refresh, setRefresh] = useState(false);

  const handleEventClick = info => {
    setModalEventContent(info);
    setIsOpenEventModal(true);
  };

  let sideTitle = [];

  const getProcessedNurseData = () => {
    let nurse = data.nurseList;
    nurse.sort((a, b) =>
      a?.firstName?.toLowerCase()?.localeCompare(b?.firstName?.toLowerCase())
    );

    sideTitle.length = 0;

    for (let index = 0; index < nurse.length; index++) {
      const element = nurse[index];
      let obj = {
        name: element?.firstName + ' ' + element?.lastName,
        value: element?.uniqueId
      };
      sideTitle.push(obj);
    }

    if (nurse.length === sideTitle.length) {
      sideTitle.unshift({
        name: 'UnAssigned Jobs',
        value: ''
      });
    }
  };

  function timeDifferentFun(sTime, eTime) {
    const startTime = moment(
      moment(new Date(sTime)).format('hh:mma'),
      'hh:mm A'
    );

    const start = moment(new Date(sTime));
    const end = moment(new Date(eTime));

    const endTime = moment(moment(new Date(eTime)).format('hh:mma'), 'hh:mm A');
    const endTimeEnd = moment(
      moment(new Date(eTime)).format('hh:mma'),
      'hh:mm A'
    ).add(1, 'day');

    const duration = moment.duration(endTime.diff(startTime));
    const durationEnd = moment.duration(endTimeEnd.diff(startTime));

    const hours = Math.floor(duration.asHours());
    const hoursEnd = Math.floor(durationEnd.asHours());
    // const minutes = Math.floor(duration.asMinutes()) - hours * 60;

    const onlyHours = `${
      end.hour() <= 12 && start.hour() <= 12
        ? hours
        : end.hour() <= 12
        ? hoursEnd
        : hours
    }`;

    return parseInt(onlyHours);
  }

  const generateOneWeekDateForJobPost = startDate => {
    const dates = [];
    const currentDate = new Date(startDate);

    // Calculate the day of the week for the provided date (0 for Sunday, 1 for Monday, ..., 6 for Saturday)
    const dayOfWeek = currentDate.getDay();

    // Calculate the date of the current Sunday (start of the week)
    const currentSunday = new Date(currentDate);
    currentSunday.setDate(currentDate.getDate() - dayOfWeek);

    // Generate dates for the entire week (Sunday to Saturday)
    for (let i = 0; i < 7; i++) {
      const nextDate = new Date(currentSunday);
      nextDate.setDate(currentSunday.getDate() + i);
      dates.push({
        date: moment(nextDate).format('YYYY-MM-DD'),
        day: moment(nextDate).format('ddd-DD'),
        dayOrder: 'day' + (i + 1)
      });
    }

    dates.unshift({
      day: 'Nurse List',
      dayOrder: 'day0'
    });

    setWeekDays(dates);
  };

  const getTableData = (nurseList, jobData, weekdays) => {
    const result = [];

    let totalData = jobData;

    nurseList.forEach(nurseItem => {
      const nurseId = nurseItem?.uniqueId;
      const nurseData = {
        id: nurseItem,
        day1: [],
        day2: [],
        day3: [],
        day4: [],
        day5: [],
        day6: [],
        day7: [],
        totalHours: 0
      };

      totalData?.forEach(dataItem => {
        // nurseData.id = nurseItem;
        if (dataItem?.jobFinalSelectionNurseId === nurseId) {
          const dateItem = weekdays?.find(
            date => date.date === dataItem?.startDate
          );
          if (dateItem) {
            const dayKey = dateItem.dayOrder;
            nurseData[dayKey].push(dataItem);
            if (!dataItem?.timeOffReason) {
              const hoursWorked = timeDifferentFun(
                dataItem?.startTime,
                dataItem?.endTime
              );
              // console.log("hoursWorked", hoursWorked)
              nurseData.totalHours += hoursWorked;
            }
          }
        }
      });
      result.push(nurseData);
    });
    //Nurse Sorting
    result.sort((a, b) =>
      a?.id?.firstName
        ?.toLowerCase()
        ?.localeCompare(b?.id?.firstName?.toLowerCase())
    );
    const openJobData = {
      id: {
        firstName: 'Unassigned',
        lastName: 'Jobs',
        uniqueId: 'not'
      },
      day1: [],
      day2: [],
      day3: [],
      day4: [],
      day5: [],
      day6: [],
      day7: []
    };
    jobData?.forEach(dataItem => {
      if (!dataItem?.jobFinalSelectionNurseId) {
        const dateItem = weekdays?.find(
          date => date.date === dataItem?.startDate
        );
        if (dateItem) {
          const dayKey = dateItem.dayOrder;
          openJobData[dayKey].push(dataItem);
        }
      }
    });

    result.unshift(openJobData);

    setResult(result);
    setLoading(false);
  };

  const getManager = async () => {
    try {
      const cusList = await getAllFacilityOrgList(
        authToken,
        location,
        organization
      );

      const list = cusList.sort((a, b) =>
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );
      let array = [];
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        let obj = {
          id: item.id,
          value: item.id,
          uniqueId: item.uniqueId,
          name: item.firstName + ' ' + item.lastName,
          label: item.firstName + ' ' + item.lastName,
          profileImage: item.profileImage,
          emailId: item.emailId,
          facilityLoginControl: item?.facilityLoginControl
        };
        array.push(obj);
      }

      if (list.length === array.length) {
        setFacilityId(array[0]?.id);
      }
    } catch (error) {
      setFacilityId(undefined);
    }
  };

  useEffect(() => {
    getProcessedNurseData();
    generateOneWeekDateForJobPost(new Date());
    getManager();
  }, []);

  const getAllData = async (authToken, userId, licenseTypes, unassigned) => {
    await getFullScheduleAllData(authToken, userId, licenseTypes, unassigned)
      .then(res => {
        if (res) {
          let jobData = res?.jobData;
          let nurseData = res?.nurseList;
          getTableData(nurseData, jobData, weekdays);
        }
      })
      .catch(e => console.log(e));
  };

  useEffect(() => {
    if (weekdays?.length === 8 && authToken && facilityId) {
      getAllData(authToken, facilityId, ['CNA', 'LPN', 'HHA', 'RN'], true);
    }
  }, [authToken, facilityId, weekdays, refresh]);

  const columns = [
    'id',
    'day1',
    'day2',
    'day3',
    'day4',
    'day5',
    'day6',
    'day7'
  ];

  const postCopyJob = async (currentDay, nurse) => {
    if (nurse?.uniqueId !== 'not') {
      const startTime = `${currentDay} ${moment(copyContent?.startTime).format(
        'hh:mm a'
      )}`;
      const endTime = `${currentDay} ${moment(copyContent?.endTime).format(
        'hh:mm a'
      )}`;

      try {
        await postJobTable(
          authToken,
          copyContent?.licenseType,
          copyContent?.shiftTitle,
          copyContent?.specialty,
          copyContent?.certification,
          copyContent?.yearOfExperience,
          copyContent?.expiration,
          moment(new Date(startTime)).format('YYYY-MM-DD'),
          moment(new Date(endTime)).format('YYYY-MM-DD'),
          new Date(startTime).toString(),
          new Date(endTime).toString(),
          copyContent?.break,
          copyContent?.customerName,
          copyContent?.customerId?.toString(),
          copyContent?.unit,
          copyContent?.floor,
          copyContent?.fullAddress,
          copyContent?.latitude?.toString(),
          copyContent?.longitude?.toString(),
          copyContent?.baseRate,
          false,
          copyContent?.notes,
          copyContent?.jobType,
          'Nurse Assigned',
          nurse?.uniqueId?.toString(),
          '',
          [nurse?.uniqueId],
          [{ id: nurse?.uniqueId, type: nurse?.poolType }],
          copyContent?.autoAcceptedEnable,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          copyContent?.jobDuration,
          copyContent?.jobTiming,
          copyContent?.jobDays,
          copyContent?.EMRorEHRExperience,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          false,
          copyContent?.specialtyRequired,
          copyContent?.experienceRequired,
          copyContent?.emrehrRequired,
          copyContent?.certificationRequired,
          copyContent?.customerVisibility,
          '',
          '',
          {},
          '',
          copyContent?.jobPostingTableFacilityTableId?.toString(),
          false,
          '',
          '',
          '',
          false,
          false,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          new Date(startTime).valueOf().toString(),
          '',
          '',
          '',
          '',
          copyContent?.organization,
          copyContent?.location_id
        ).then(async item => {
          // console.log(item);
          let date = `${moment(item?.startTime).format('hh:mm a')}-${moment(
            item?.endTime
          ).format('hh:mm a')}`;
          setCopyContent(undefined);
          socket?.emit('tableJobInsert', {
            table: 'JobPostingTable',
            message: 'Fetch Dashboard Job'
          });

          if (nurse?.mobileId) {
            sendNotification(authToken, {
              expoPushToken: nurse?.mobileId,
              title: `“Direct ${item?.jobType} Assignment” (Check Upcoming Jobs)`,
              body: `${item?.jobType} for ${DateFormat(
                item?.startDate
              )} ${date}`,
              data: { jobId: item?.id, screen: 'JobDetailScreen' }
            });
          }
          await postNurseNotification(
            authToken,
            nurse?.id,
            '',
            `“Direct ${item?.jobType} Assignment” (Check Upcoming Jobs)`,
            `${item?.jobType} for ${DateFormat(item?.startDate)} ${date}`,
            'JobDetailsScreen',
            {
              id: item?.id
            },
            false,
            false,
            'green',
            ''
          );
          socket?.emit('tableNotificationInsert', {
            table: 'NurseNotificationTable',
            id: nurse?.id
          });
          toast.success(`Assigned successfully.`, {
            theme: 'colored'
          });
          setRefresh(!refresh);
        });
      } catch (error) {
        console.log('data save error:', error);
      }
    }
  };

  const moveCardHandler = (currentItem, newDropItem) => {
    if (
      moment(newDropItem?.date).format('MM/DD/YYYYY') >=
      moment().format('MM/DD/YYYYY')
    ) {
      if (newDropItem?.nurseId !== 'not') {
        const updatedResult = result?.map(job => {
          let currentDay = currentItem?.currentColumnName;
          let newDay = newDropItem?.day;

          let obj = job[currentDay]?.find(i => i?.id === currentItem?.id);

          let newNurse = result.find(
            nurseJob => nurseJob?.id?.uniqueId === newDropItem?.nurseId
          );

          if (obj && newNurse) {
            let check = overLapCheck(
              newDropItem?.date,
              newDropItem?.endDate,
              obj?.startTime,
              obj?.endTime,
              newNurse[newDay]
            );

            if (check?.availableDate && check.availableTime) {
              let newObj = {
                ...obj,
                jobFinalSelectionNurseId: newDropItem?.nurseId,
                jobStatus: 'Nurse Assigned'
              };

              job[currentDay] = job[currentDay]?.filter(
                i => i?.id !== currentItem?.id
              );

              newNurse[newDay] = [...(newNurse[newDay] || []), newObj];

              job.totalHours = calculateTotalHours(job);

              newNurse.totalHours = calculateTotalHours(newNurse);
            }

            return job;
          }
          return job;
        });

        setResult(updatedResult);
      }
    } else {
      toast.warning(`You can't assign the job to past date!`, {
        theme: 'colored'
      });
    }
  };

  const returnItemsForDay = (dayKey, nurseId, handleEventClick) => {
    // Find the nurse's data based on the uniqueId
    const nurseData = result.find(nurse => nurse.id.uniqueId === nurseId);

    if (!nurseData) return null;

    // Fetch the items for the specified dayKey (column)
    const dayItems = nurseData?.[dayKey] || [];

    // Map through dayItems and render each one
    return dayItems.map((item, index) => (
      <MovableItem
        key={item.id}
        index={index}
        name={item?.shiftTitle}
        item={item}
        currentColumnName={dayKey}
        moveCardHandler={moveCardHandler}
        result={result}
        setResult={setResult}
        handleEventClick={handleEventClick}
        setCopyContent={setCopyContent}
      />
    ));
  };

  const renderNurseDayItems = (item, column, weekdays, handleEventClick) => {
    // Find day and date based on the column
    const currentDay = weekdays?.find(date => date.dayOrder === column);
    const dayOrder = currentDay?.dayOrder;

    // Render nurse items only once for each day/nurse combination
    const dayItems = returnItemsForDay(
      dayOrder,
      item['id']?.uniqueId,
      handleEventClick
    );

    const pasteItem = () => {
      if (
        moment(currentDay?.date).format('MM/DD/YYYYY') >=
        moment().format('MM/DD/YYYYY')
      ) {
        postCopyJob(currentDay?.date, item['id']);
      } else {
        console.log('Else');
      }
    };

    return (
      <div
        style={{
          flex: 1,
          height: '100%',
          backgroundColor: '#f2f2f2'
        }}
        onClick={() => pasteItem()}
      >
        {dayItems?.length > 0 ? (
          dayItems
        ) : (
          <div
            style={{
              padding: 10,
              backgroundColor: '#f2f2f2',
              height: '100%'
            }}
          >
            No Items
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        height: 500,
        cursor: copyContent === undefined ? '' : 'grabbing'
      }}
    >
      {loading ? (
        <p>Loading...</p>
      ) : (
        weekdays &&
        result && (
          <div style={{ marginLeft: -20 }}>
            <div
              className=""
              style={{ display: 'flex', marginTop: -18, marginBottom: -16 }}
            >
              {weekdays?.map((item, index) => (
                <Flex key={index}>
                  <p
                    style={{
                      width: 230,
                      padding: 10,
                      textAlign: 'center',
                      backgroundColor: '#ccc',
                      fontWeight: '700'
                    }}
                  >
                    {item?.day === 'Nurse List' ? 'Nurse/Status' : item?.day}
                  </p>
                </Flex>
              ))}
            </div>
            <Flex>
              <div
                style={{
                  flex: 1
                }}
              >
                {result?.map((nurseItem, index) => {
                  return (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        display: 'flex'
                      }}
                    >
                      {columns.map((column, indexC) => {
                        return (
                          <div
                            key={indexC}
                            style={{
                              width: 230,
                              borderStyle: 'solid',
                              borderWidth: 0.1,
                              borderColor: 'black',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            {column === 'id' &&
                            nurseItem?.hasOwnProperty('id') ? (
                              <div
                                style={{
                                  width: 180,
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <div
                                  style={{
                                    marginLeft: 10,
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}
                                >
                                  <p
                                    style={{
                                      color: '#000',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {nurseItem['id']?.firstName +
                                      ' ' +
                                      nurseItem['id']?.lastName}
                                  </p>
                                  {nurseItem['id']?.firstName !==
                                    'Unassigned' && (
                                    <p
                                      style={{
                                        color: '#000',
                                        fontWeight: '500'
                                      }}
                                    >
                                      {nurseItem['id']?.primaryLicenseType} -{' '}
                                      <span className="text-primary">
                                        {nurseItem['totalHours']}h
                                      </span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  flex: 1,
                                  height: '100%'
                                }}
                              >
                                <Column
                                  key={index}
                                  title={''}
                                  value={nurseItem['id']?.uniqueId}
                                  className={`flex-1`}
                                  date={
                                    weekdays?.find(
                                      date => date.dayOrder === column
                                    )?.date
                                  }
                                  day={
                                    weekdays?.find(
                                      date => date.dayOrder === column
                                    )?.dayOrder
                                  }
                                >
                                  {renderNurseDayItems(
                                    nurseItem,
                                    column,
                                    weekdays,
                                    handleEventClick
                                  )}
                                </Column>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </Flex>
          </div>
        )
      )}

      <CalendarEventModal
        isOpenEventModal={isOpenEventModal}
        setIsOpenEventModal={setIsOpenEventModal}
        modalEventContent={modalEventContent}
      />
    </div>
  );
};

Column.propTypes = {
  children: PropTypes.any,
  className: PropTypes.any,
  title: PropTypes.any,
  value: PropTypes.any,
  date: PropTypes.any,
  day: PropTypes.any
};

MovableItem.propTypes = {
  index: PropTypes.any,
  name: PropTypes.any,
  item: PropTypes.any,
  currentColumnName: PropTypes.any,
  moveCardHandler: PropTypes.any,
  result: PropTypes.any,
  setResult: PropTypes.any,
  handleEventClick: PropTypes.any,
  setCopyContent: PropTypes.any
};

export default withDragDropContext(RosterPlan);
